// An Assessment that is still being built: what the manager has entered in Run
// Assessment so far. The backend keeps it from the moment Details is complete
// (PUT /v3/admin/capability/assessment-drafts/{id}), so the dashboard shows it
// as a card at Design and its Continue reopens the flow at the step it was
// left. Running it, or discarding it, removes it.
//
// Who sees one, and who may change one, is the backend's rule: an org admin and
// every manager of a team it measures see it; an org admin and a manager of
// every team it measures may change it (`canEdit`).
//
// This is the shape the flow keeps, and how it goes to the backend and comes
// back. No React here, so it can be tested on its own.

import type { AssessmentDraftView, SaveAssessmentDraftInput } from '../../../../../api/capability';
import type { FlowDetails } from './DetailsStep';
import type { DraftItem } from './assessmentDraft';

export type AssessmentStep = 'details' | 'capabilities' | 'summary' | 'review';
export type AssessmentSchedule = 'none' | 'monthly' | 'quarterly' | 'halfYearly' | 'yearly';

const STEPS: AssessmentStep[] = ['details', 'capabilities', 'summary', 'review'];
const SCHEDULES: AssessmentSchedule[] = ['none', 'monthly', 'quarterly', 'halfYearly', 'yearly'];

/** Run Assessment's state, as it is kept. */
export interface AssessmentDraftState {
  v: 1;
  /** The step to reopen on. */
  step: AssessmentStep;
  /** Details, with the date as text. */
  details: Omit<FlowDetails, 'publishBy'> & { publishBy: string | null };
  /** null = every capability of the roles in scope, until the manager unticks one. */
  capabilityIds: string[] | null;
  schedule: AssessmentSchedule;
  startOnPublish: boolean;
  firstRun: string | null;
  /** Elliott's draft as the manager edited it, and the capabilities it was written for. */
  written: { key: string; items: DraftItem[] } | null;
  message: string | null;
}

export interface AssessmentDraft extends Omit<AssessmentDraftState, 'v'> {
  id: string;
  /** The owner's name, for the card. */
  ownerName: string | null;
  /** The viewer may carry on building it. */
  canEdit: boolean;
  /** The teams of the roles it measures, as the backend has them. */
  teamIds: string[];
}

/** What the flow holds, as it is kept. */
export const draftOf = (state: {
  id: string;
  step: AssessmentStep;
  details: FlowDetails;
  capabilityIds: string[] | null;
  schedule: AssessmentSchedule;
  startOnPublish: boolean;
  firstRun: Date | null;
  written: { key: string; items: DraftItem[] } | null;
  message: string | null;
  ownerName: string | null;
}): AssessmentDraft => ({
  id: state.id,
  step: state.step,
  details: { ...state.details, publishBy: state.details.publishBy ? state.details.publishBy.toISOString() : null },
  capabilityIds: state.capabilityIds,
  schedule: state.schedule,
  startOnPublish: state.startOnPublish,
  firstRun: state.firstRun ? state.firstRun.toISOString() : null,
  written: state.written,
  message: state.message,
  ownerName: state.ownerName,
  // The flow only keeps what its own manager may change; its teams are the backend's to say.
  canEdit: true,
  teamIds: []
});

/** What the backend is sent to keep a draft. */
export const draftBody = (draft: AssessmentDraft): SaveAssessmentDraftInput => ({
  name: draft.details.name.trim(),
  stageId: draft.details.stageId,
  roleIds: draft.details.roleIds,
  ownerName: draft.ownerName,
  state: {
    v: 1,
    step: draft.step,
    details: draft.details,
    capabilityIds: draft.capabilityIds,
    schedule: draft.schedule,
    startOnPublish: draft.startOnPublish,
    firstRun: draft.firstRun,
    written: draft.written,
    message: draft.message
  }
});

/** A draft as the backend returns it, or null when its state is not one this flow can reopen. */
export const draftFromView = (view: AssessmentDraftView): AssessmentDraft | null => {
  const state = view.state as Partial<AssessmentDraftState> | null;
  if (!state || state.v !== 1 || typeof state.details !== 'object' || state.details === null) return null;
  return {
    id: view.id,
    step: STEPS.includes(state.step as AssessmentStep) ? (state.step as AssessmentStep) : 'details',
    details: state.details,
    capabilityIds: Array.isArray(state.capabilityIds) ? state.capabilityIds : null,
    schedule: SCHEDULES.includes(state.schedule as AssessmentSchedule)
      ? (state.schedule as AssessmentSchedule)
      : 'quarterly',
    startOnPublish: state.startOnPublish !== false,
    firstRun: state.firstRun ?? null,
    written: state.written ?? null,
    message: state.message ?? null,
    ownerName: view.ownerName,
    canEdit: view.canEdit,
    teamIds: view.teamIds
  };
};

const dateOf = (text: string | null): Date | null => {
  const date = text ? new Date(text) : null;
  return date && !Number.isNaN(date.getTime()) ? date : null;
};

/** The Details a draft holds, with its date back as a date. */
export const detailsOfDraft = (draft: AssessmentDraft): FlowDetails => ({
  ...draft.details,
  publishBy: dateOf(draft.details.publishBy)
});

/** The first run's date a draft holds, as a date. */
export const firstRunOfDraft = (draft: AssessmentDraft): Date | null => dateOf(draft.firstRun);

const PREFIX = 'assessment-draft:';
export const isAssessmentDraftId = (id: string): boolean => id.startsWith(PREFIX);
/** A new draft's id: the backend accepts lower-case letters and digits after the prefix. */
export const newDraftId = (): string => `${PREFIX}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
